import React from "react"

/** The payload `Analytics.recordEvent` — and now PostHog — expects. */
export interface AnalyticsEvent {
  /**
   * Optional because `AppProvider.recordEvent` fills it in from the store when
   * a caller leaves it out, which is how `useRecordEvent` avoids needing it.
   * The class components still pass it explicitly.
   */
  pub_id?: number
  event_name: string
  event_data?: Record<string, unknown>
}

export type RecordEvent = (event: AnalyticsEvent) => void

/**
 * Carries `AppProvider`'s `recordEvent` down the tree.
 *
 * The class components hand it to their children as a prop and spread it
 * onward with `{...this.props}`, which is why a leaf like `Nav` can call it
 * without ever asking for it. TypeScript components get it from here instead —
 * see `useRecordEvent`.
 */
export const RecordEventContext = React.createContext<RecordEvent | null>(null)

/**
 * Records an analytics event from a function component.
 *
 * Wraps `AppProvider.recordEvent` so callers pass only what differs between
 * events: `pub_id` is filled in by `AppProvider`, and the payload's shape is
 * the hook's problem rather than every call site's.
 *
 *   const recordEvent = useRecordEvent()
 *   recordEvent("Cancellation Feedback", { reason: reason.label })
 *
 * Both the legacy queue and PostHog are fed, because `AppProvider` is still the
 * single place that does either.
 *
 * Deliberately context-only: react-redux is pinned to a release that predates
 * the installed React, and `connect()` is what the rest of the app uses, so
 * this stays clear of react-redux's hooks.
 *
 * Recording is a no-op outside an `AppProvider` — the same silence the class
 * components' `if (this.props.recordEvent)` guards give a component rendered
 * without the prop.
 */
export function useRecordEvent() {
  const recordEvent = React.useContext(RecordEventContext)

  return React.useCallback(
    (eventName: string, eventData?: Record<string, unknown>) => {
      if (!recordEvent) return

      recordEvent({
        event_name: eventName,
        event_data: eventData,
      })
    },
    [recordEvent]
  )
}
